import React from 'react';
import { observer } from 'mobx-react';
import {
    FlexDiv,
    SpaceBetweenDiv,
    Block,
    Image,
    TextDefault,
    TextLead,
    Heading2,
    palette,
    ThemedButton,
} from '@awarego-ui/components';
import {ButtonBase} from "@mui/material";
import Link from "@mui/material/Link";
import ThemedSpinner from '../themed/ThemedSpinner';
import {
    ThemedNav,
    ThemedNavItem,
    ThemedNavLink,
} from '../themed/ThemedComponents';

function CompanyAppearanceEditor({
    onEditLogo,
    onEditColor,
    onResetToDefaults,
    company_id,
    company,
    saving,
}) {
    const [activeNavItem, setActiveNavItem] = React.useState(0);
    
    const primarySetting =
        company.uiSettings?.['ui.colors.primary'] ??
        company.settings?.['ui.colors.primary'] ??
        null;

    const secondarySetting =
        company.uiSettings?.['ui.colors.secondary'] ??
        company.settings?.['ui.colors.secondary'] ??
        null;

    return (
        <FlexDiv>
            <Block>
                <FlexDiv column>
                    <Heading2>Customize appearance</Heading2>
                    <TextDefault>
                        Change the appearance of {company.company_name} for
                        employees (employee view).
                    </TextDefault>
                </FlexDiv>
                <SpaceBetweenDiv bottomBorder py={16} mt={8}>
                    <FlexDiv column mr={16}>
                        <TextLead bold>Company Logo</TextLead>
                        <TextDefault>
                            Set a custom logo that will be shown on employee
                            view when they sign in.
                        </TextDefault>
                    </FlexDiv>
                    <ButtonBase
                        onClick={() => onEditLogo(company_id)}
                        disableRipple={true}
                        style={{flexDirection: 'column'}}
                    >
                        {company.logo_name ? (
                            <Image
                                width={150}
                                src={
                                    import.meta.env.REACT_APP_RESOURCES_URL +
                                    '/' +
                                    import.meta.env
                                        .REACT_APP_COMPANY_RESOURCES_DIR +
                                    '/' +
                                    company.logo_name
                                }
                                alt="Your logo"
                            />
                        ) : (
                            <Image
                                width={50}
                                src="/static/img/logo-image.svg"
                                alt="Add your logo"
                            />
                        )}
                        <TextDefault themed bold>
                            Change
                        </TextDefault>
                    </ButtonBase>
                </SpaceBetweenDiv>
                <SpaceBetweenDiv bottomBorder py={16} mb={24}>
                    <FlexDiv column mr={16}>
                        <TextLead bold>Colors</TextLead>
                        <TextDefault>
                            Change the colors of main buttons, links and navigation highlights to your
                            brand colors.
                        </TextDefault>
                    </FlexDiv>
                    <ButtonBase
                        onClick={() => onEditColor(company_id)}
                        disableRipple={true}
                        style={{flexDirection: 'column'}}
                    >
                        <FlexDiv gap={8}>
                            <FlexDiv
                                backgroundColor={
                                    primarySetting || palette.evergreen.aspargus
                                }
                                width={32}
                                minHeight={32}
                                border
                                borderRadius={4}
                            />
                            <FlexDiv
                                backgroundColor={
                                    secondarySetting || palette.vibrant.raspberry
                                }
                                width={32}
                                minHeight={32}
                                border
                                borderRadius={4}
                            />
                        </FlexDiv>
                        <TextDefault themed bold>
                            Change
                        </TextDefault>
                    </ButtonBase>
                </SpaceBetweenDiv>

                <Link
                    component="button"
                    onClick={() => onResetToDefaults(company_id)}
                >
                    <TextDefault
                        error
                        bold
                    >
                        Reset to default
                    </TextDefault>
                </Link>
            </Block>
            <Block ml={20}>
                <Heading2>Preview</Heading2>
                <TextDefault>
                    This is how buttons and navigation will look in the employee view:
                </TextDefault>
                {saving ? (
                    <FlexDiv marginAuto column alignCenter justifyCenter style={{ minHeight: '200px' }}>
                        <ThemedSpinner />
                    </FlexDiv>
                ) : (
                    <>
                        <FlexDiv marginAuto column>
                            <FlexDiv mb={8}>
                                <ThemedButton
                                    color={primarySetting || palette.evergreen.aspargus}
                                    mode="primary"
                                    text="Button"
                                />
                            </FlexDiv>
                            <FlexDiv mb={16}>
                                <ThemedButton
                                    color={primarySetting || palette.evergreen.aspargus}
                                    mode="secondary"
                                    text="Button"
                                />
                            </FlexDiv>

                        </FlexDiv>
                        <FlexDiv justifyCenter>
                            <ThemedNav clean>
                                <ThemedNavItem 
                                    active={activeNavItem === 0} 
                                    color={secondarySetting || palette.vibrant.raspberry}
                                    onClick={() => setActiveNavItem(0)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <ThemedNavLink>
                                        Navigation Item
                                    </ThemedNavLink>
                                </ThemedNavItem>
                                <ThemedNavItem 
                                    active={activeNavItem === 1} 
                                    color={secondarySetting || palette.vibrant.raspberry}
                                    onClick={() => setActiveNavItem(1)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <ThemedNavLink>
                                        Navigation Item
                                    </ThemedNavLink>
                                </ThemedNavItem>
                            </ThemedNav>
                        </FlexDiv>
                    </>
                )}
            </Block>
        </FlexDiv>
    );
}

export default observer(CompanyAppearanceEditor);
